import React, { useState } from "react";
import { G, TYPE, FONT_FAMILY } from "../../constants/theme";

// Section-level "View Score Breakdown" disclosure. Explains the factors and
// weights that make up a score/ranking shown in a section header — separate
// from any per-ticket breakdown, which stays exactly as implemented.
//
// A factor is either:
//   { label, points }                      — one line: label ....... value chip
//   { label, tiers: [{ name, value }, …] } — a titled group, one row per tier
//                                            (name left, value chip right)
//
// When any factor is grouped (the three Work-Requiring-Attention header cards),
// the whole panel switches to one uniform, monochrome blue treatment so it
// reads as part of the theme rather than a plain grey box.
export default function ScoreBreakdown({ title, intro, factors = [], footnote, dark = false, style = {} }) {
  const [open, setOpen] = useState(false);

  const grouped = factors.some((f) => Array.isArray(f.tiers));
  const blue = !dark && grouped; // themed blue treatment

  // ── Palette ───────────────────────────────────────────────────────────────
  // "blue" mode uses only tokens that already appear across the dashboard:
  // navy #071A2F header strips (like every section card), brandSubtle #EEF4FF
  // surface, brandBorder #BFDBFE, blue50 / blue100, brand #2563EB, blue700
  // #1D4ED8, and #93C5FD as the on-navy accent.
  const toggleFg = blue ? G.blue700 : dark ? "rgba(255,255,255,0.55)" : G.gray500;
  const toggleFgHover = dark ? "#FFFFFF" : G.blue700;
  const toggleBorder = blue
    ? `1px solid ${G.brandBorder}`
    : dark
    ? "1px solid rgba(255,255,255,0.15)"
    : `1px solid ${G.border}`;

  const panelBg = blue ? G.brandSubtle : dark ? "rgba(255,255,255,0.05)" : G.gray50;
  const panelBorder = blue
    ? `1px solid ${G.brandBorder}`
    : dark
    ? "1px solid rgba(255,255,255,0.1)"
    : `1px solid ${G.border}`;

  const cardBg = dark ? "rgba(255,255,255,0.03)" : G.white;
  const cardBorder = blue
    ? `1px solid ${G.brandBorder}`
    : dark
    ? "1px solid rgba(255,255,255,0.08)"
    : `1px solid ${G.border}`;
  const divider = blue
    ? `1px solid ${G.blue100}`
    : dark
    ? "1px solid rgba(255,255,255,0.08)"
    : `1px solid ${G.border}`;

  // Group title strip — navy, echoing the section-card headers on the page
  const stripBg = blue ? G.dark : dark ? "rgba(255,255,255,0.04)" : G.gray50;
  const stripFg = blue ? "#93C5FD" : dark ? "rgba(255,255,255,0.3)" : G.gray400;

  const headColor = dark ? "rgba(255,255,255,0.85)" : G.gray800;
  const bodyColor = blue ? G.gray700 : dark ? "rgba(255,255,255,0.55)" : G.gray600;
  const mutedColor = blue ? G.gray500 : dark ? "rgba(255,255,255,0.3)" : G.gray400;
  const titleColor = blue ? G.blue700 : dark ? "rgba(255,255,255,0.3)" : G.gray400;

  const dotColor = blue ? G.brand : dark ? "rgba(255,255,255,0.35)" : G.gray400;

  // Value chip — one style everywhere in a grouped/blue panel
  const chip = blue
    ? { background: G.blue50, color: G.blue700, border: `1px solid ${G.brandBorder}` }
    : dark
    ? { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)", border: "1px solid rgba(255,255,255,0.12)" }
    : { background: G.gray100, color: G.gray800, border: `1px solid ${G.gray200}` };

  const ChipValue = ({ children }) => (
    <span
      style={{
        fontFamily: TYPE.mono.fontFamily,
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 4,
        padding: "2px 8px",
        flexShrink: 0,
        whiteSpace: "nowrap",
        ...chip,
      }}
    >
      {children}
    </span>
  );

  return (
    <div style={{ fontFamily: FONT_FAMILY, ...style }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: toggleBorder,
          borderRadius: 4,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 600,
          color: toggleFg,
          cursor: "pointer",
          fontFamily: FONT_FAMILY,
          letterSpacing: "0.02em",
          whiteSpace: "nowrap",
          transition: "color 0.12s, border-color 0.12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = toggleFgHover; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = toggleFg; }}
      >
        {open ? "Hide Score Breakdown ▲" : "View Score Breakdown ▾"}
      </button>

      {open && (
        <div style={{ marginTop: 10, padding: "14px 16px", borderRadius: 6, background: panelBg, border: panelBorder, maxWidth: 480 }}>
          {title && (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: titleColor, marginBottom: 8 }}>
              {title}
            </div>
          )}
          {intro && (
            <div style={{ fontSize: 12, color: bodyColor, lineHeight: 1.55, marginBottom: 12 }}>{intro}</div>
          )}

          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
            {factors.map((f, i) => {
              // ── Grouped factor — vertical tier list ──────────────────────
              if (Array.isArray(f.tiers)) {
                return (
                  <li key={i} style={{ listStyle: "none" }}>
                    <div style={{ border: cardBorder, borderRadius: 6, overflow: "hidden", background: cardBg }}>
                      <div style={{
                        padding: "7px 12px",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        color: stripFg,
                        background: stripBg,
                        borderBottom: blue ? `1px solid ${G.darkBorder}` : divider,
                      }}>
                        {f.label}
                      </div>
                      {f.tiers.map((t, j) => (
                        <div key={j} style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          padding: "6px 12px",
                          borderTop: j === 0 ? "none" : divider,
                        }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: dotColor }} />
                            <span style={{ fontSize: 12, color: bodyColor }}>{t.name}</span>
                          </span>
                          <ChipValue>{t.value}</ChipValue>
                        </div>
                      ))}
                    </div>
                  </li>
                );
              }

              // ── Simple factor — single line ─────────────────────────────
              // In a grouped/blue panel it gets the same chip so the panel
              // stays uniform; elsewhere it keeps the plain inline value.
              if (blue) {
                return (
                  <li key={i} style={{
                    listStyle: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    border: cardBorder,
                    borderRadius: 6,
                    background: cardBg,
                    padding: "8px 12px",
                  }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: dotColor }} />
                      <span style={{ fontSize: 12, color: bodyColor }}>{f.label}</span>
                    </span>
                    <ChipValue>{f.points}</ChipValue>
                  </li>
                );
              }

              return (
                <li key={i} style={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                  <span style={{ color: bodyColor }}>{f.label}</span>
                  <span style={{ fontWeight: 700, color: headColor, fontFamily: TYPE.mono.fontFamily, fontSize: 12, flexShrink: 0 }}>
                    {f.points}
                  </span>
                </li>
              );
            })}
          </ul>

          {footnote && (
            <div style={{ fontSize: 11, color: mutedColor, marginTop: 12, lineHeight: 1.5, borderTop: divider, paddingTop: 10 }}>
              {footnote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
